const VISITOR_KEY = "byteline_visitor_id";
const SESSION_KEY = "byteline_session_id";
const SESSION_TS_KEY = "byteline_session_last";
const SESSION_COUNT_KEY = "byteline_session_pages";
const SESSION_TTL_MS = 30 * 60 * 1000;

const uuid = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const getVisitorId = (): string => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return uuid();
  }
};

/** Session id, rolled over after 30 minutes of inactivity. */
export const getSessionId = (): { sessionId: string; pageIndex: number } => {
  try {
    const now = Date.now();
    const last = Number(localStorage.getItem(SESSION_TS_KEY) || 0);
    let id = localStorage.getItem(SESSION_KEY);
    let count = Number(localStorage.getItem(SESSION_COUNT_KEY) || 0);

    if (!id || !last || now - last > SESSION_TTL_MS) {
      id = uuid();
      count = 0;
      localStorage.setItem(SESSION_KEY, id);
    }

    count += 1;
    localStorage.setItem(SESSION_TS_KEY, String(now));
    localStorage.setItem(SESSION_COUNT_KEY, String(count));
    return { sessionId: id, pageIndex: count };
  } catch {
    return { sessionId: uuid(), pageIndex: 1 };
  }
};

/** Current session id without counting a new page. */
export const peekSessionId = (): string | null => {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
};

export interface ClientEnvironment {
  deviceType: string;
  browser: string;
  os: string;
  country: string | null;
  region: string | null;
  screenWidth: number | null;
  screenHeight: number | null;
  viewportWidth: number | null;
  viewportHeight: number | null;
  displayMode: string;
  language: string | null;
}

const resolveDisplayMode = (): string => {
  if (typeof window === "undefined") return "browser";
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone) return "installed app";
  try {
    if (window.matchMedia("(display-mode: standalone)").matches) return "installed app";
    if (window.matchMedia("(display-mode: minimal-ui)").matches) return "installed app";
  } catch {
    /* matchMedia unavailable */
  }
  return "browser";
};

/** Device, browser, system and an approximate location from the browser time zone. */
export const getClientEnvironment = (): ClientEnvironment => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";

  const isTablet = /iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua));
  const isMobile = !isTablet && /Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua);
  const deviceType = isTablet ? "tablet" : isMobile ? "mobile" : "desktop";

  let browser = "Other";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR\//i.test(ua) || /Opera/i.test(ua)) browser = "Opera";
  else if (/SamsungBrowser/i.test(ua)) browser = "Samsung Internet";
  else if (/Firefox\//i.test(ua)) browser = "Firefox";
  else if (/Chrome\//i.test(ua)) browser = "Chrome";
  else if (/Safari\//i.test(ua)) browser = "Safari";

  let os = "Other";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Mac OS X/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";

  let country: string | null = null;
  let region: string | null = null;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("/")) {
      const [area, ...rest] = tz.split("/");
      region = rest.join("/").replace(/_/g, " ");
      country = TZ_COUNTRY[tz] || area;
    }
  } catch {
    country = null;
  }

  return {
    deviceType,
    browser,
    os,
    country,
    region,
    screenWidth: typeof window !== "undefined" ? window.screen?.width ?? null : null,
    screenHeight: typeof window !== "undefined" ? window.screen?.height ?? null : null,
    viewportWidth: typeof window !== "undefined" ? window.innerWidth : null,
    viewportHeight: typeof window !== "undefined" ? window.innerHeight : null,
    displayMode: resolveDisplayMode(),
    language: typeof navigator !== "undefined" ? navigator.language || null : null,
  };
};

/** Time zones mapped to the countries that matter most for reporting. */
const TZ_COUNTRY: Record<string, string> = {
  "America/New_York": "United States",
  "America/Chicago": "United States",
  "America/Denver": "United States",
  "America/Phoenix": "United States",
  "America/Los_Angeles": "United States",
  "America/Anchorage": "United States",
  "Pacific/Honolulu": "United States",
  "America/Toronto": "Canada",
  "America/Vancouver": "Canada",
  "America/Edmonton": "Canada",
  "America/Sao_Paulo": "Brazil",
  "America/Mexico_City": "Mexico",
  "America/Bogota": "Colombia",
  "America/Argentina/Buenos_Aires": "Argentina",
  "Europe/London": "United Kingdom",
  "Europe/Dublin": "Ireland",
  "Europe/Paris": "France",
  "Europe/Berlin": "Germany",
  "Europe/Madrid": "Spain",
  "Europe/Rome": "Italy",
  "Europe/Amsterdam": "Netherlands",
  "Europe/Brussels": "Belgium",
  "Europe/Zurich": "Switzerland",
  "Europe/Vienna": "Austria",
  "Europe/Stockholm": "Sweden",
  "Europe/Oslo": "Norway",
  "Europe/Copenhagen": "Denmark",
  "Europe/Helsinki": "Finland",
  "Europe/Warsaw": "Poland",
  "Europe/Prague": "Czechia",
  "Europe/Lisbon": "Portugal",
  "Europe/Athens": "Greece",
  "Europe/Istanbul": "Turkey",
  "Europe/Moscow": "Russia",
  "Europe/Kyiv": "Ukraine",
  "Asia/Dubai": "United Arab Emirates",
  "Asia/Riyadh": "Saudi Arabia",
  "Asia/Qatar": "Qatar",
  "Asia/Kuwait": "Kuwait",
  "Asia/Bahrain": "Bahrain",
  "Asia/Muscat": "Oman",
  "Asia/Karachi": "Pakistan",
  "Asia/Kolkata": "India",
  "Asia/Calcutta": "India",
  "Asia/Dhaka": "Bangladesh",
  "Asia/Bangkok": "Thailand",
  "Asia/Jakarta": "Indonesia",
  "Asia/Manila": "Philippines",
  "Asia/Singapore": "Singapore",
  "Asia/Kuala_Lumpur": "Malaysia",
  "Asia/Hong_Kong": "Hong Kong",
  "Asia/Shanghai": "China",
  "Asia/Taipei": "Taiwan",
  "Asia/Seoul": "South Korea",
  "Asia/Tokyo": "Japan",
  "Asia/Jerusalem": "Israel",
  "Africa/Cairo": "Egypt",
  "Africa/Lagos": "Nigeria",
  "Africa/Nairobi": "Kenya",
  "Africa/Johannesburg": "South Africa",
  "Australia/Sydney": "Australia",
  "Australia/Melbourne": "Australia",
  "Australia/Perth": "Australia",
  "Australia/Brisbane": "Australia",
  "Pacific/Auckland": "New Zealand",
};
